<template>
    <v-dialog :model-value="dialog" @click:outside="closeModal" @keydown.esc="closeModal" max-width="600px"
        transition="dialog-bottom-transition">
        <v-card @keydown.enter="handleEnterKey">
            <v-form @submit.prevent="submitAttributes" ref="form" v-model="valid">
                <v-card-title class="d-flex align-center">
                    <span class="headline">Atributos del Producto</span>
                </v-card-title>

                <v-card-text>
                    <v-alert  variant="tonal" class="mb-4">
                        <div class="d-flex align-center">
                            <v-icon class="mr-2">mdi-information</v-icon>
                            <div>
                                <h5 class="font-weight-bold">{{ product?.name }}</h5>
                                <h5 class="">SKU: {{ product?.sku }}</h5>
                            </div>
                        </div>
                    </v-alert>

                    <div class="space-y-4">
                        <v-combobox v-if="product && product.tiene_control_por_lotes" 
                            ref="loteInput"
                            autocomplete="off" 
                            name="lote" 
                            label="Lote *" 
                            v-model="attributes.lote"
                            :items="availableLotes"
                            item-title="name"
                            item-value="name"
                            :rules="[rules.required]"
                            @keyup.enter="focusNextField('lote')"
                            @update:model-value="onLoteChanged"
                            clearable />

                        <v-text-field v-if="product && product.tiene_control_por_serie" 
                            ref="serieInput"
                            autocomplete="off" 
                            name="serie" 
                            label="Nº Serie *" 
                            type="text" 
                            v-model="attributes.serie"
                            :rules="[rules.required]"
                            @keyup.enter="focusNextField('serie')" />

                        <v-text-field v-if="product && product.tiene_control_por_descriptivo_1" 
                            ref="descriptivo1Input"
                            autocomplete="off"
                            name="descriptivo_1" 
                            :label="getAliasName('descriptivo_1') + ' *'" 
                            type="text"
                            v-model="attributes.descriptivo_1"
                            :rules="[rules.required]"
                            @keyup.enter="focusNextField('descriptivo_1')" />

                        <v-text-field v-if="product && product.tiene_control_por_descriptivo_2" 
                            ref="descriptivo2Input"
                            autocomplete="off"
                            name="descriptivo_2" 
                            :label="getAliasName('descriptivo_2') + ' *'" 
                            type="text"
                            v-model="attributes.descriptivo_2"
                            :rules="[rules.required]"
                            @keyup.enter="focusNextField('descriptivo_2')" />

                        <v-text-field v-if="product && product.tiene_control_por_descriptivo_3" 
                            ref="descriptivo3Input"
                            autocomplete="off"
                            name="descriptivo_3" 
                            :label="getAliasName('descriptivo_3') + ' *'" 
                            type="text"
                            v-model="attributes.descriptivo_3"
                            :rules="[rules.required]"
                            @keyup.enter="focusNextField('descriptivo_3')" />

                        <!-- Calidad -->
                        <v-combobox v-if="product && product.tiene_control_de_calidad" 
                            ref="qualityInput"
                            v-model="attributes.quality" 
                            name="quality"
                            item-title="name" 
                            item-value="id" 
                            :items="qualities" 
                            label="Calidad *" 
                            autocomplete="off"
                            :rules="[rules.requiredComboBox]" 
                            @keyup.enter="focusNextField('quality')"
                            clearable />

                        <v-text-field v-if="product && 
                            product.storage_unit &&
                            product.storage_unit.id != productoService.STORAGE_UNITS_ID
                        " 
                            ref="storageInput"
                            autocomplete="off" 
                            name="storage_units_amount" 
                            label="Valor de almacenamiento *" 
                            type="number"
                            v-model="attributes.storage_units_amount"
                            :rules="[rules.required]"
                            @keyup.enter="focusNextField('storage_units_amount')" />

                        <v-combobox v-if="product && product.requiere_fecha_expiracion" 
                            ref="expirationInput"
                            v-model="attributes.expiration_date"
                            name="expiration_date" 
                            label="Fecha de Expiración *" 
                            :items="availableExpirationDates"
                            item-title="name"
                            item-value="name"
                            :rules="[rules.required]"
                            @keyup.enter="focusNextField('expiration_date')"
                            @update:model-value="onExpirationDateChanged"
                            clearable />
                    </div>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="error" variant="text" @click="closeModal">
                        Cancelar
                    </v-btn>
                    <v-btn color="success" variant="text" type="submit" :loading="loading" :disabled="!isFormValid">
                        Confirmar
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script>
import { productoService } from "@/libs/ws/productoService";
import { productQualityService } from "@/libs/ws/productQualityService";
import ValidMixin from "@/mixins/ValidMixin.vue";
import AliasMixin from "@/mixins/AliasMixin.vue";


export default {
    name: "ModalProductAttributes",
    mixins: [ValidMixin, AliasMixin],

    props: {
        dialog: {
            type: Boolean,
            default: false,
        },
        product: {
            type: Object,
            default: null,
        },
        lpnData: {
            type: Object,
            default: null,
        },
    },

    emits: ["close", "submit"],

    data() {
        return {
            valid: false,
            loading: false,
            productoService: productoService,
            attributes: {
                lote: "",
                serie: "",
                descriptivo_1: "",
                descriptivo_2: "",
                descriptivo_3: "",
                expiration_date: "",
                manufacture_date: "",
                storage_units_amount: 0,
                quality: null,
            },
            qualities: [],
            availableLotes: [],
            availableExpirationDates: [],
            dateToLoteMapping: {},
        };
    },

    computed: {
        showManufactureDate() {
            return (
                this.product?.requiere_fecha_expiracion ||
                this.attributes.expiration_date
            );
        },

        requiredFields() {
            const fields = [];
            if (this.product?.tiene_control_por_lotes) fields.push('lote');
            if (this.product?.tiene_control_por_serie) fields.push('serie');
            if (this.product?.tiene_control_por_descriptivo_1) fields.push('descriptivo_1');
            if (this.product?.tiene_control_por_descriptivo_2) fields.push('descriptivo_2');
            if (this.product?.tiene_control_por_descriptivo_3) fields.push('descriptivo_3');
            if (this.product?.tiene_control_de_calidad) fields.push('quality');
            if (this.product?.storage_unit && this.product.storage_unit.id != this.productoService.STORAGE_UNITS_ID) fields.push('storage_units_amount');
            if (this.product?.requiere_fecha_expiracion) fields.push('expiration_date');
            return fields;
        },

        fieldOrder() {
            return ['lote', 'serie', 'descriptivo_1', 'descriptivo_2', 'descriptivo_3', 'quality', 'storage_units_amount', 'expiration_date'];
        },

        isFormValid() {
            return this.requiredFields.every(field => {
                const value = this.attributes[field];
                if (field === 'quality') {
                    return value && (typeof value === 'object' ? value.id : value);
                }
                return value !== '' && value !== null && value !== undefined;
            });
        }
    },

    watch: {
        dialog(newVal) {
            console.log("Dialog changed:", newVal);
            if (newVal) {
                this.resetForm();
                this.loadQualities();
                this.loadComboBoxLoteAndExpiration();
                console.log("Dialog opened, resetting form and loading qualities");
                this.$nextTick(() => {
                    this.focusFirstField();
                });
            }
        },
    },

    methods: {
        resetForm() {
            this.attributes = {
                lote: null,
                serie: "",
                descriptivo_1: "",
                descriptivo_2: "",
                descriptivo_3: "",
                expiration_date: null,
                manufacture_date: null,
                storage_units_amount: null,
                quality: null,
            };
            this.availableLotes = [];
            this.availableExpirationDates = [];
            this.dateToLoteMapping = {};
            this.valid = false;
        },

        focusFirstField() {
            this.$nextTick(() => {
                const firstField = this.requiredFields[0];
                if (firstField) {
                    this.focusField(firstField);
                }
            });
        },

        focusField(fieldName) {
            const refMap = {
                'lote': 'loteInput',
                'serie': 'serieInput',
                'descriptivo_1': 'descriptivo1Input',
                'descriptivo_2': 'descriptivo2Input',
                'descriptivo_3': 'descriptivo3Input',
                'quality': 'qualityInput',
                'storage_units_amount': 'storageInput',
                'expiration_date': 'expirationInput'
            };

            const refName = refMap[fieldName];
            if (refName && this.$refs[refName]) {
                this.$refs[refName].focus();
            }
        },

        focusNextField(currentField) {
            const currentIndex = this.fieldOrder.indexOf(currentField);
            
            // Buscar el próximo campo requerido
            for (let i = currentIndex + 1; i < this.fieldOrder.length; i++) {
                const nextField = this.fieldOrder[i];
                if (this.requiredFields.includes(nextField)) {
                    this.focusField(nextField);
                    return;
                }
            }
            
            if (this.isFormValid) {
                this.submitAttributes();
            }
        },

      

        loadQualities() {
            productQualityService
                .filter({ product: this.product.id })
                .then((response) => {
                    this.qualities = response.result.list;
                });
        },

        loadComboBoxLoteAndExpiration() {
            if (this.lpnData?.assigned_locations) {
                const lotes = [];
                const expirationDates = [];
                const dateToLoteMapping = {};
                
                // Filtrar assigned_locations por el producto escaneado usando SKU
                const filteredLocations = this.lpnData.assigned_locations.filter((item) => 
                    item.presentation_atributes?.product_presentation?.product?.sku === this.product?.sku
                );
                
                filteredLocations.forEach((item) => {
                    if (item.presentation_atributes) {
                        const lote = item.presentation_atributes.lote;                  
                        const expDate = item.presentation_atributes.expiration_date;
   
                        if (lote && !lotes.includes(lote)) {
                            lotes.push(lote);
                        }
                        if (expDate && !expirationDates.includes(expDate)) {
                            expirationDates.push(expDate);
                        }
                        
                        // Crear mapeo entre fecha y lote
                        if (expDate && lote) {
                            dateToLoteMapping[expDate] = lote;
                        }
                    }
                });
                 
                // Ordenar fechas de expiración de menor a mayor
                expirationDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
                
                // Convertir a formato para combobox
                const lotesMapped = lotes.map((lote, index) => ({
                    id: index + 1,
                    name: lote
                }));
                
                const expirationDatesMapped = expirationDates.map((date, index) => ({
                    id: index + 1,
                    name: date
                }));
                
                this.availableLotes = lotesMapped;
                this.availableExpirationDates = expirationDatesMapped;
                this.dateToLoteMapping = dateToLoteMapping;
                
                // Auto-seleccionar si hay una sola opción
                if (expirationDates.length === 1 && this.product?.requiere_fecha_expiracion) {
                    this.attributes.expiration_date = expirationDates[0];
                }
                
                if (lotes.length === 1 && this.product?.tiene_control_por_lotes) {
                    this.attributes.lote = lotes[0];
                }
            } 
        },

        onLoteChanged(value) {
            // Obtener el valor del lote (puede ser un objeto o string)
            const loteValue = typeof value === 'object' ? value?.name : value;
            
            // Cuando se selecciona un lote, buscar si hay una fecha de expiración asociada
            if (loteValue && this.product?.requiere_fecha_expiracion) {
                const associatedDate = Object.keys(this.dateToLoteMapping).find(
                    date => this.dateToLoteMapping[date] === loteValue
                );
                
                if (associatedDate && !this.attributes.expiration_date) {
                    this.attributes.expiration_date = associatedDate;
                }
            }
        },

        onExpirationDateChanged(value) {    
            // Obtener el valor de la fecha (puede ser un objeto o string)
            const dateValue = typeof value === 'object' ? value?.name : value;
            
            // Cuando se selecciona una fecha de expiración, buscar si hay un lote asociado
            if (dateValue && this.product?.tiene_control_por_lotes) {
                const associatedLote = this.dateToLoteMapping[dateValue];
                
                if (associatedLote && !this.attributes.lote) {
                    this.attributes.lote = associatedLote;
                }
            }
        },

        submitAttributes() {
            if (!this.isFormValid || this.loading) return;

            this.loading = true;

            // Limpiar atributos vacíos
            const cleanAttributes = {};
            Object.keys(this.attributes).forEach((key) => {
                if (this.attributes[key] !== "" && this.attributes[key] !== null && this.attributes[key] !== undefined) {
                    cleanAttributes[key] = this.attributes[key];
                }
            });

            // Simular procesamiento
            setTimeout(() => {
                this.loading = false;
                this.$emit("submit", cleanAttributes);
            }, 500);
        },

        closeModal() {
            this.$emit("close");
        },

        handleEnterKey(event) {
            // Si no estamos en un input específico y el formulario está válido, submit
            if (event.target.tagName !== 'INPUT' && this.isFormValid) {
                this.submitAttributes();
            }
        },
    },
};
</script>

<style scoped>
.space-y-4>*+* {
    margin-top: 16px;
}
</style>
